import React, { useMemo } from "react"

import { FormProvider, useForm } from "react-hook-form"

import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"

import { CoverageCheckerRunningState } from "../../../config/checker.config"
import { useAppDispatch } from "../../../store/appDispatch.hook.ts"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { checkerSelector } from "../../../store/selectors/checker.selector"
import { checkerSetModalityCode } from "../../../store/slices/checker.slice.ts"
import type { IDynamicCheckerFormInputData } from "../../../types/data.types.ts"
import { CheckTypeSelectorComponent } from "../checkTypeSelector.component"
import { CheckerDynamicFormComponent } from "./checkerDynamicForm.component"
import { CheckerResultsComponent } from "./checkerResults.component"
import { ModalityCodeInput } from "./inputs/modalityCode.input"

export const CoverageCheckerManualComponent: React.FunctionComponent<
    ICoverageCheckerManualComponentProps
> = props => {
    const formMethods = useForm<IDynamicCheckerFormInputData>()

    const checkerState = useAppSelector(checkerSelector)
    const dispatch = useAppDispatch()

    const isInputView = useMemo<boolean>(() => {
        return [CoverageCheckerRunningState.INPUT_MANUAL].includes(
            checkerState.runningState,
        )
    }, [checkerState.runningState])

    const title = useMemo<JSX.Element | null>(() => {
        switch (checkerState.runningState) {
            case CoverageCheckerRunningState.INPUT_MANUAL:
                return (
                    <>
                        Fill out the information you have below, or choose one
                        of the preconfigured check types.
                    </>
                )

            default:
                return null
        }
    }, [checkerState.runningState])

    const getCheckerByRunningState = (): JSX.Element | null => {
        switch (checkerState.runningState) {
            case CoverageCheckerRunningState.INPUT_MANUAL:
                return (
                    <CheckerDynamicFormComponent resetForm={props.resetForm} />
                )

            case CoverageCheckerRunningState.RESULTS_MANUAL:
                return <CheckerResultsComponent />

            default:
                return null
        }
    }

    return (
        <div
            className={`
                relative
                ${props.className}
            `}
        >
            <FormProvider {...formMethods}>
                {checkerState.runningState ===
                CoverageCheckerRunningState.INPUT_MANUAL ? (
                    <ModalityCodeInput
                        className="mt-[48px] w-[287px]"
                        onChange={value => {
                            if (!value) {
                                return
                            }

                            dispatch(checkerSetModalityCode(value))
                        }}
                        defaultValue={checkerState.modalityCode}
                    />
                ) : null}

                {title ? (
                    <PrimaryText weight="medium" className="mt-[20px]">
                        {title}
                    </PrimaryText>
                ) : null}

                <div className="mt-[20px] flex items-stretch">
                    {isInputView ? (
                        <CheckTypeSelectorComponent className="w-[220px] rounded-l-lg" />
                    ) : null}

                    <div
                        className={`
                            relative p-[16px] bg-brand-offWhite2
                            ${
                                !isInputView
                                    ? "flex-1"
                                    : "w-[540px] rounded-r-lg overflow-hidden"
                            }
                        `}
                    >
                        {getCheckerByRunningState()}
                    </div>
                </div>
            </FormProvider>
        </div>
    )
}

interface ICoverageCheckerManualComponentProps {
    className?: string
    resetForm: () => void
}
